// src/context/SnackbarContext.jsx
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';

// One app-wide toast. Before this existed, useSnackbar() held local state that no
// caller ever rendered, so every showSuccess/showError in the app was silently
// dropped — including messages fired right before a navigate(), which would have
// been lost with the unmounting page anyway.
const SnackbarContext = createContext(null);

// Callers pass API error payloads straight through; never hand React an object.
const toText = (message) => {
  if (typeof message === 'string') return message;
  if (message == null) return '';
  return message.message || message.detail || JSON.stringify(message);
};

export function SnackbarProvider({ children }) {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
    autoHideDuration: 6000,
    key: 0,
  });

  const show = useCallback((text, severity, autoHideDuration) => {
    setSnackbar((prev) => ({
      open: true,
      message: text,
      severity,
      autoHideDuration,
      // New key restarts the auto-hide timer when toasts arrive back to back
      key: prev.key + 1,
    }));
  }, []);

  const showSnackbar = useCallback((message, severity = 'info', autoHideDuration = 6000) => {
    const text = toText(message);
    if (severity === 'error') {
      // Forms show server errors in a banner next to the fields (FormPage's
      // `error`); many also toast the same message. Once the page has rendered,
      // skip the toast if that exact message is already on screen.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const onPage = [...document.querySelectorAll('.MuiAlert-standardError .MuiAlert-message')]
          .some((el) => el.textContent.trim() === text.trim());
        if (!onPage) show(text, severity, autoHideDuration);
      }));
      return;
    }
    show(text, severity, autoHideDuration);
  }, [show]);

  const hideSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  // Only the stable functions go in context, so showing a toast doesn't
  // re-render the ~100 components that can trigger one.
  const actions = useMemo(() => ({ showSnackbar, hideSnackbar }), [showSnackbar, hideSnackbar]);

  const handleClose = (_event, reason) => {
    if (reason === 'clickaway') return;
    hideSnackbar();
  };

  return (
    <SnackbarContext.Provider value={actions}>
      {children}
      <Snackbar
        key={snackbar.key}
        open={snackbar.open}
        autoHideDuration={snackbar.autoHideDuration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
}

export function useSnackbarContext() {
  return useContext(SnackbarContext);
}
